import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import './GuideView.css';
import Container from 'react-bootstrap/Container';

interface Heading {
  level: number;
  text: string;
  id: string;
}

const GuideView: React.FC = () => {
  const [markdown, setMarkdown] = useState<string>('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [tocCollapsed, setTocCollapsed] = useState<boolean>(true);

  useEffect(() => {
    fetch('/guide.md')
      .then(res => res.text())
      .then(text => setMarkdown(text))
      .catch(err => {
        console.error('Error loading guide content:', err);
        setMarkdown('# Hacker Guide: uOttaHack 8\n\nContent loading...');
      });
  }, []);

  // Extract headings from markdown for TOC
  const headings = useMemo(() => {
    if (!markdown) return [];
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const matches: Heading[] = [];
    let match;
    
    while ((match = headingRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      // Skip h1 headings from TOC
      if (level === 1) continue;
      
      const text = match[2].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      
      matches.push({ level, text, id });
    }
    
    return matches;
  }, [markdown]);

  // Initialize all sections as collapsed on mount
  useEffect(() => {
    if (headings.length > 0) {
      const allSectionIds = new Set(headings.map(h => h.id));
      setCollapsedSections(allSectionIds);
    }
  }, [headings]);

  const toggleSection = (id: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const openAllSections = () => {
    setCollapsedSections(new Set());
    setTocCollapsed(false);
  };

  const closeAllSections = () => {
    if (headings.length > 0) {
      const allSectionIds = new Set(headings.map(h => h.id));
      setCollapsedSections(allSectionIds);
      setTocCollapsed(true);
    }
  };

  // Extract intro content (h1 and content before first h2)
  const { introContent, mainSections } = useMemo(() => {
    if (!markdown) return { introContent: '', mainSections: [] };
    
    const lines = markdown.split('\n');
    const introLines: string[] = [];
    const mainSections: Array<{ heading: Heading | null; content: string[] }> = [];
    let foundFirstH2 = false;
    let currentSection: { heading: Heading | null; content: string[] } = { heading: null, content: [] };
    
    lines.forEach((line) => {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const text = headingMatch[2].trim();
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-');
        
        // Add h1 and everything before first h2 to intro
        if (level === 1) {
          introLines.push(line);
        } else if (level === 2 && !foundFirstH2) {
          foundFirstH2 = true;
          // Start new section for this h2
          if (currentSection.heading || currentSection.content.length > 0) {
            mainSections.push(currentSection);
          }
          currentSection = {
            heading: { level, text, id },
            content: []
          };
        } else if (!foundFirstH2) {
          // Before first h2, add to intro (but skip h1)
          introLines.push(line);
        } else {
          // After first h2, process normally
          if (currentSection.heading || currentSection.content.length > 0) {
            mainSections.push(currentSection);
          }
          currentSection = {
            heading: { level, text, id },
            content: []
          };
        }
      } else {
        if (!foundFirstH2) {
          introLines.push(line);
        } else {
          currentSection.content.push(line);
        }
      }
    });
    if (currentSection.heading || currentSection.content.length > 0) {
      mainSections.push(currentSection);
    }
    
    return {
      introContent: introLines.join('\n'),
      mainSections
    };
  }, [markdown]);

  return (
    <Container id="guide-view" fluid>
      <div className="guide-content-wrapper">
        <div className="guide-content">
          {/* Intro Content (Title and first paragraph) */}
          {introContent && (
            <div className="guide-intro">
              <ReactMarkdown>{introContent}</ReactMarkdown>
              <div className="section-controls">
                <button 
                  className="section-control-btn open-all-btn"
                  onClick={openAllSections}
                >
                  Open All
                </button>
                <button 
                  className="section-control-btn close-all-btn"
                  onClick={closeAllSections}
                >
                  Close All
                </button>
              </div>
            </div>
          )}
          
          {/* Table of Contents */}
          {headings.length > 0 && (
            <div className="table-of-contents">
              <h3 
                className="toc-title collapsible-toc-heading"
                onClick={() => setTocCollapsed(!tocCollapsed)}
              >
                <span className={`collapse-icon ${tocCollapsed ? 'collapsed' : ''}`}>▼</span>
                <span className="heading-text">Table of Contents</span>
              </h3>
              <div className={`toc-content ${tocCollapsed ? 'collapsed' : ''}`}>
                <ul className="toc-list">
                  {headings.map((heading, idx) => (
                    <li 
                      key={idx} 
                      className={`toc-item toc-level-${heading.level}`}
                    >
                      <a 
                        href={`#${heading.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById(heading.id);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Main Content with Collapsible Sections */}
          <div className="guide-markdown-content">
            {mainSections.map((section, idx) => {
              if (!section.heading) {
                const content = section.content.join('\n').trim();
                if (!content) return null;
                return (
                  <div key={idx} className="section-no-heading">
                    <ReactMarkdown>{content}</ReactMarkdown>
                  </div>
                );
              }
              
              const isCollapsed = collapsedSections.has(section.heading.id);
              const HeadingTag = `h${section.heading.level}` as keyof JSX.IntrinsicElements;
              const content = section.content.join('\n').trim();
              
              return (
                <div key={`${section.heading.id}-${idx}`} className="collapsible-section">
                  <HeadingTag 
                    id={section.heading.id}
                    className="collapsible-heading"
                    onClick={() => toggleSection(section.heading!.id)}
                  >
                    <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
                    <span className="heading-text">{section.heading.text}</span>
                  </HeadingTag>
                  {content && (
                    <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                      <ReactMarkdown>{content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default GuideView;
